import { getRouteApi } from '@tanstack/react-router';
import { Modal } from 'cpool-components/general/Modal';
import type { LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { TextWithCopy } from '~/components/TextWithCopy';
import type { OfferEntry } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory/schemas/offers.schema';
import { EuropaceProcessEventsDialogTitle } from '~/features/leadDetails/components/LeadSideInfo/ExternalServices/Europace/EuropaceProcessEventsDialog/EuropaceProcessEventsDialogTitle';
import { EuropaceProcessEventsTable } from '~/features/leadDetails/components/LeadSideInfo/ExternalServices/Europace/EuropaceProcessEventsDialog/Table/EuropaceProcessEventsTable';
import { useEuropaceProcessEventsMutation } from '~/features/leadDetails/components/LeadSideInfo/ExternalServices/hooks/useEuropaceProcessEventsMutation';
import useLead from '~/features/leadDetails/hooks/useLead';

const SIDEBAR_WIDTH = '522px';

type Props = {
    offer:
        | (SchemaLoanRequestResolver_GetAcceptedOffers[number] & {
              source: typeof LOAN_REQUEST_OFFER_SOURCES.EUROPACE | typeof LOAN_REQUEST_OFFER_SOURCES.EUROPACE_SYNC;
          })
        | OfferEntry;
    isOpen: boolean;
    onClose: () => void;
    europaceOfferId?: string | null;
};

export const EuropaceOfferInfoDialog: React.FC<Props> = ({ europaceOfferId, isOpen, offer, onClose }) => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const { isPending: isEventsRequestPending, mutate: requestEuropaceProcessEvents } =
        useEuropaceProcessEventsMutation({
            leadId,
            processId: lead?.europace?.id,
        });

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            title={t('OFFER_MANAGEMENT.OFFER_EXTRA_INFO.TITLE')}
            variant="sidebar"
            side="right"
            width={SIDEBAR_WIDTH}
        >
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pt-4">
                <TextWithCopy
                    text={offer.id}
                    tooltipBeforeCopyText={t('OFFER_MANAGEMENT.ICONS_TOOLTIP.COPY_OFFER_ID')}
                    tooltipAfterCopyText={t('OFFER_MANAGEMENT.COPIED_TO_CLIPBOARD')}
                    className="text-sm text-grey"
                >
                    {t('OFFER_MANAGEMENT.OFFER_EXTRA_INFO.OFFER_ID')}: {offer.id}
                </TextWithCopy>
                <EuropaceProcessEventsDialogTitle
                    isPending={isEventsRequestPending}
                    requestEuropaceProcessEvents={requestEuropaceProcessEvents}
                />
                <EuropaceProcessEventsTable europaceOfferId={europaceOfferId} />
            </div>
        </Modal>
    );
};
